import React, { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { Box, Typography, styled } from '@mui/material';

import PrimaryBtn from 'components/shared/Buttons/PrimaryButton';
import { AddUpdateCommentTemplate } from 'components/comment-templates/AddUpdateCommentTemplateModal';
import { TableLoadingLayout } from 'components/layouts/LoadingLayouts/TableLoadingLayout';
import { GeneralTable } from 'components/shared/Table/GeneralTable';
import CommentTemplateRow from './CommentTemplateRow';

import useGeneralDialog from 'hooks/dialogs/useGeneralDialog';
import useCommentTemplatesSWR from 'hooks/commentTemplates/useCommentTemplatesSWR';
import useCommentTemplates from 'hooks/commentTemplates/useCommentTemplates';

import type {
    ComponentTemplateHeadCellContent,
    CommentTemplateWithAction,
} from 'models/CommentTemplate';

import { COMMENT_TEMPLATE_HEAD_CELLS } from 'utils/constans/tableHeads/commentTemplatesTableHead';

const StyledRow = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
}));

const TABLE_HEAD_SETTINGS = {
    translateNs: 'commentTemplates',
    translatePrefix: 'TABLE_HEAD',
    selectable: false,
    headContent: COMMENT_TEMPLATE_HEAD_CELLS,
};

const TABLE_TOOLBAR_SETTINGS = {
    translateNs: 'commentTemplates',
    translatePrefix: '',
};

export const CommentTemplates: React.FC = () => {
    const { t } = useTranslation('commentTemplates');

    const { comments, isLoading, isError, forceRefresh } =
        useCommentTemplatesSWR();

    const { deleteCommentTemplate } = useCommentTemplates();

    const generalDialog = useGeneralDialog();

    const handleDelete = useCallback(
        async (id: number): Promise<void> => {
            await deleteCommentTemplate(id);
            forceRefresh();
        },
        [deleteCommentTemplate]
    );

    const commentTemplatesWithAction = useMemo(
        (): CommentTemplateWithAction[] =>
            comments?.map(
                (commentTemplate): CommentTemplateWithAction => ({
                    ...commentTemplate,
                    deleteAction: handleDelete.bind(this, commentTemplate.id),
                })
            ),
        [comments, handleDelete]
    );

    const handleAddNewCommentTemplate = (): void => {
        generalDialog.showDialog({
            title: t('ADD_MODAL.TITLE'),
            confirmLabel: t('ADD_MODAL.CONFIRM'),
            closeLabel: t('ADD_MODAL.CLOSE'),
            component: AddUpdateCommentTemplate,
            onCloseWithSuccess: forceRefresh,
            preSetContent: {note: '', title: ''},
        });
    };

    const handleFilter = useCallback(
        (search): CommentTemplateWithAction[] => {
            return commentTemplatesWithAction.filter((elem): boolean => {
                return (
                    elem.id.toString().includes(search.toLowerCase()) ||
                    elem.title.toString().includes(search)
                );
            });
        },
        [commentTemplatesWithAction]
    );

    return (
        <Box>
            <StyledRow>
                <Typography variant="h1">{t('TITLE')}</Typography>
                <PrimaryBtn onClick={handleAddNewCommentTemplate}>
                    {t('ADD_MODAL.TITLE')}
                </PrimaryBtn>
            </StyledRow>
            {isError && <Box>{t('LOADING_ERROR')}</Box>}
            {isLoading && <TableLoadingLayout />}
            {!isLoading && comments && (
                <GeneralTable<
                    CommentTemplateWithAction,
                    ComponentTemplateHeadCellContent
                >
                    fullList={commentTemplatesWithAction}
                    rowComponent={CommentTemplateRow}
                    filterTerm={handleFilter}
                    defaultOrderBy={'created_at'}
                    defaultOrder="desc"
                    selectHandleId="id"
                    tableHeadSettings={TABLE_HEAD_SETTINGS}
                    tableToolbarSettings={TABLE_TOOLBAR_SETTINGS}
                    forceRefresh={forceRefresh}
                    isLoading={isLoading}
                />
            )}
        </Box>
    );
};
